import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as consts from 'config/consts';
import { useParams } from 'react-router-dom';
import Loading from 'components/commons/loading/Loading';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useRouting } from 'hooks/useRouting';
import { registerTarget } from 'config/api';
import { useModal } from 'hooks/useModal';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useBirthday } from '../utils/birthday';
import { TargetType } from '../utils/type';
import {
  targetTypeToImg,
  targetTypeToTitle,
  targetTypes,
} from '../utils/targetInfo';
import { TargetValidateError, validateTarget } from '../utils/validate';
import { useTargetSupportRouting } from '../hooks/useTargetSupportRouting';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';
import { InputRow } from '../components/commons/InputRow';
import { SetPropertiesBase } from '../components/features/share/SetPropertiesBase';
import { useTargetTitleForm } from '../hooks/useTargetTitleForm';
import { useTargetAmountForm } from '../hooks/useTargetAmountForm';
import { useTargetAgeOrYearForm } from '../hooks/useTargetAgeOrYear';
import { ConfirmQuitModal } from '../components/features/create/ConfirmQuitModal';
import { dateToString } from '../utils/utils';

type RegisterTargetResponseProps =
  | { tag: 'Success'; id: string }
  | { tag: 'Error' };

const TargetCreateInternal = ({
  birthday,
  targetType,
}: {
  birthday: Date;
  targetType: TargetType;
}) => {
  const { goTargetIndex, goTargetReplaceSelect } = useTargetSupportRouting();

  const [modalType, setModalType] = useState<'button' | 'header'>('button');

  const [isShowModal, { hideModal, showModal }] = useModal();

  const [errors_, setErrors] = useState<
    | { tag: 'errorDisabled' }
    | { tag: 'errorEnabled'; errors: TargetValidateError[] }
  >({ tag: 'errorDisabled' });
  const errors = (() => {
    switch (errors_.tag) {
      case 'errorDisabled':
        return null;
      case 'errorEnabled':
        return errors_.errors;
    }
  })();

  const titleErrors = useMemo(
    () =>
      (errors ?? []).flatMap(e => (e.tag === 'TitleError' ? [e.error] : [])),
    [errors]
  );

  const targetTypeInfo = targetTypes.find(x => x.targetType === targetType);

  const { TargetTitleForm, targetTitle } = useTargetTitleForm(
    titleErrors,
    targetTypeInfo?.title
  );

  const amountErrors = useMemo(
    () =>
      (errors ?? []).flatMap(e => (e.tag === 'AmountError' ? [e.error] : [])),
    [errors]
  );

  const roughTargetAmount = targetTypeInfo?.roughTargetAmount;

  const initialTargetAmount =
    roughTargetAmount === null || roughTargetAmount === undefined
      ? 0
      : roughTargetAmount;

  const { TargetAmountForm, targetAmount } = useTargetAmountForm({
    amountErrors,
    initialTargetAmount,
  });

  const dateErrors = useMemo(
    () => (errors ?? []).flatMap(e => (e.tag === 'DateError' ? [e.error] : [])),
    [errors]
  );

  const { TargetAgeOrYearForm, Switch, absYear } = useTargetAgeOrYearForm({
    birthday,
    dateErrors,
  });

  const target = useMemo(
    () =>
      validateTarget({
        targetType,
        targetTitle,
        targetAmount,
        assetAmount: 0,
        targetYear: absYear,
        achieved: false,
        thisYear: new Date().getFullYear(),
        birthYear: birthday.getFullYear(),
      }),
    [absYear, targetAmount, targetTitle, targetType, birthday]
  );

  useEffect(() => {
    setErrors(() => {
      return {
        tag: 'errorEnabled',
        errors: target.tag === 'Error' ? target.errors : [],
      };
    });
  }, [target]);

  const [apiState, setAPIState] = useState<'ready' | 'hitting' | 'failed'>(
    'ready'
  );

  const finishButtonMessage = useMemo((): React.ReactElement => {
    switch (apiState) {
      case 'ready':
        return <>この内容で登録する</>;
      case 'hitting':
        return (
          <>
            <Loading loading circularLoading />
          </>
        );
      case 'failed':
        return <>この内容で再試行する</>;
    }
  }, [apiState]);

  const showConfirmQuitModal = (type: 'button' | 'header'): void => {
    setModalType(type);
    showModal();
  };

  const routing = useRouting();
  const { setBack } = useCustomizeHeaderContext();
  useEffect(() => {
    setBack({
      backFunction: () => showConfirmQuitModal('header'),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFinish = useCallback(async (): Promise<void> => {
    switch (target.tag) {
      case 'Success':
        setAPIState('hitting');
        const ret = await registerTargetAPI({
          ...target.value,
          startDate: new Date(),
          endDate: new Date(target.value.endDate),
        });
        switch (ret.tag) {
          case 'Success':
            routing.replaceRouting(
              { path: `/target/${ret.id}/register` },
              true
            );
            break;
          case 'Error':
            setAPIState('failed');
            break;
        }
        break;
      case 'Error':
        setErrors({ tag: 'errorEnabled', errors: target.errors });
        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return (
    <>
      <SetPropertiesBase
        {...{
          finishButton: {
            message: finishButtonMessage,
            isDisabled: target.tag === 'Error',
          },
          onFinish,
          onCancel: () => showConfirmQuitModal('button'),
          imgPath: targetTypeToImg(targetType),
          imgOverStr: targetTypeToTitle(targetType),
        }}>
        <InputRow {...{ title: 'タイトル' }} className={`mb-8`}>
          {TargetTitleForm}
        </InputRow>
        <InputRow {...{ title: '目標金額（単位：万円）' }} className={`mb-8`}>
          {TargetAmountForm}
        </InputRow>
        <InputRow
          {...{
            title: '達成時期',
            SubInformation: Switch,
          }}
          className={``}>
          {TargetAgeOrYearForm}
        </InputRow>
      </SetPropertiesBase>
      <ConfirmQuitModal
        {...{
          hide: hideModal,
          isShowModal,
          onQuit:
            modalType === 'button' ? goTargetIndex : goTargetReplaceSelect,
          onContinue: hideModal,
          modalType,
        }}
      />
    </>
  );
};

const TargetCreate = (): React.ReactElement => {
  const { setWithBackIconWord, setActionPattern } = useCustomizeHeaderContext();
  setActionPattern(consts.HeaderActionPatternType.なし);
  useEffect(() => {
    setWithBackIconWord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useCustomHeaderTitle('目標の設定');
  const { type: type_ } = useParams<{ type: string }>();
  const targetType = Number(type_) as TargetType;

  const { birthday } = useBirthday();

  if (birthday === null || birthday === undefined) {
    return (
      <PremiumCheck content={<TargetPremium />}>
        <NotFoundBirthday />
      </PremiumCheck>
    );
  }

  return (
    <PremiumCheck content={<TargetPremium />}>
      <TargetCreateInternal {...{ birthday, targetType }} />
    </PremiumCheck>
  );
};

type RegisterTargetProps = {
  refType: TargetType;
  title: string;
  targetAmount: number;
  startDate: Date;
  endDate: Date;
};

const registerTargetAPI = async (
  target: RegisterTargetProps
): Promise<RegisterTargetResponseProps> => {
  const ret = await registerTarget({
    EndDate: dateToString(target.endDate),
    RefType: target.refType,
    StartDate: dateToString(target.startDate),
    TargetAmount: target.targetAmount,
    Title: target.title,
  });

  return ret.ResultCode === 0
    ? { tag: 'Success', id: ret.ID }
    : { tag: 'Error' };
};

export default TargetCreate;
